import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Wallet, ArrowLeft, CreditCard, History, CheckCircle, MinusCircle, Phone, Zap, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WAVE_MERCHANT_LINK = process.env.REACT_APP_WAVE_MERCHANT_LINK;

const CREDIT_AMOUNTS = [500, 1000, 2000, 5000, 10000, 25000];

const PAYMENT_METHODS = [
  { id: "wave", name: "Wave", color: "bg-blue-500" },
  { id: "orange_money", name: "Orange Money", color: "bg-orange-500" },
  { id: "free_money", name: "Free Money", color: "bg-green-500" }
];

export default function CreditsPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [creditBalance, setCreditBalance] = useState(0);
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPayTechLoading, setIsPayTechLoading] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [paymentPhone, setPaymentPhone] = useState(user?.phone || "");
  const [showHistory, setShowHistory] = useState(false);
  const [showWaveConfirm, setShowWaveConfirm] = useState(false);
  const [pendingTransactionId, setPendingTransactionId] = useState(null);

  const fetchBalance = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/credits/balance/${user.id}`);
      setCreditBalance(response.data.credit_balance || 0);
    } catch (error) {
      console.error("Error:", error);
    }
  }, [user.id]);

  const fetchHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/credits/history/${user.id}`);
      setHistory(response.data);
    } catch (error) {
      console.error("Error:", error);
    }
  }, [user.id]);

  // === Retour PayTech : lecture des query params success/cancel ===
  useEffect(() => {
    const paytechResult = searchParams.get("paytech");
    const ref = searchParams.get("ref");
    if (!paytechResult || !ref) return;

    if (paytechResult === "success") {
      // Poll 3x (l'IPN peut arriver avec un petit délai)
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const r = await axios.get(`${API}/payments/paytech/status/${ref}`);
          if (r.data?.status === "completed") {
            toast.success(
              `Paiement reçu ! Votre nouveau solde est de ${Number(r.data.balance_after).toLocaleString("fr-FR")} FCFA.`
            );
            fetchBalance();
            fetchHistory();
            setSearchParams({}, { replace: true });
            return;
          }
        } catch (err) {
          console.warn("poll status check failed:", err);
        }
        if (tries < 6) setTimeout(poll, 2000);
        else {
          toast.info("Paiement en cours de confirmation. Solde mis à jour dans quelques secondes.");
          fetchBalance();
          fetchHistory();
          setSearchParams({}, { replace: true });
        }
      };
      poll();
    } else if (paytechResult === "cancel") {
      toast.error("Paiement annulé");
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    fetchBalance();
    fetchHistory();
  }, [fetchBalance, fetchHistory]);

  const handleRechargePayTech = async (amount) => {
    if (!amount || amount < 100) {
      toast.error("Montant invalide (min 100 FCFA)");
      return;
    }
    setIsPayTechLoading(true);
    try {
      const r = await axios.post(`${API}/payments/paytech/create`, {
        user_id: user.id,
        amount,
        description: "Recharge crédits ARTEKNOS",
        phone_number: paymentPhone || user.phone,
      });
      if (r.data?.redirect_url) {
        toast.success("Redirection vers PayTech…");
        window.location.href = r.data.redirect_url;
      } else {
        toast.error("Échec de l'initiation du paiement");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Service de paiement indisponible");
    } finally {
      setIsPayTechLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (!selectedAmount) {
      toast.error("Choisissez un montant");
      return;
    }
    if (!selectedMethod) {
      toast.error("Choisissez un mode de paiement");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/credits/purchase`, {
        user_id: user.id,
        amount: selectedAmount,
        payment_method: selectedMethod,
        payment_phone: paymentPhone
      });

      if (response.data.status === "pending_wave" && response.data.wave_payment_url) {
        // Ouvrir Wave dans un nouvel onglet
        window.open(response.data.wave_payment_url, "_blank");
        setPendingTransactionId(response.data.transaction_id);
        setShowWaveConfirm(true);
        toast.info("Redirigé vers Wave. Payez puis confirmez ici.");
      } else if (response.data.status === "pending_verification") {
        toast.info(response.data.message);
        setPendingTransactionId(response.data.transaction_id);
        setShowWaveConfirm(true);
      } else {
        // Cash - crédit immédiat
        toast.success(response.data.message);
        setCreditBalance(response.data.new_balance);
        setSelectedAmount(null);
        setSelectedMethod(null);
        fetchHistory();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'achat");
    } finally {
      setIsLoading(false);
    }
  };

  const confirmWavePayment = async () => {
    if (!pendingTransactionId) return;
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/credits/confirm/${pendingTransactionId}`);
      toast.success(response.data.message);
      if (response.data.new_balance !== undefined) setCreditBalance(response.data.new_balance);
      setShowWaveConfirm(false);
      setPendingTransactionId(null);
      setSelectedAmount(null);
      setSelectedMethod(null);
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur de confirmation");
    } finally {
      setIsLoading(false);
    }
  };

  const getDashboardPath = () => {
    if (user.role === 'courier') return '/courier';
    if (user.role === 'partner') return '/partner';
    return '/dashboard';
  };

  return (
    <div className="min-h-screen bg-[#0F172A] text-white">
      {/* Header */}
      <nav className="bg-[#1E293B] border-b border-white/5 text-white px-4 md:px-8 py-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate(getDashboardPath())}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/10 rounded-full"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-black uppercase tracking-tight text-white flex items-center gap-2" style={{fontFamily: 'Outfit, sans-serif'}}>
                <Wallet className="w-5 h-5 text-[#D4AF37]" />
                Crédits ARTEKNOS
              </h1>
              <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold">{user.full_name}</p>
            </div>
          </div>
          <Button onClick={onLogout} variant="outline" className="rounded-full text-white border-white/20 bg-transparent hover:bg-white/10 hover:text-[#D4AF37] hover:border-[#D4AF37]">
            Déconnexion
          </Button>
        </div>
      </nav>

      <div className="px-4 md:px-8 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Balance Card */}
          <Card className="mb-6 bg-gradient-to-br from-[#D4AF37] to-[#B8962E] text-[#0F172A] border-0 shadow-xl shadow-[#D4AF37]/20 rounded-[2rem]" data-testid="balance-card">
            <CardContent className="pt-8 pb-8 text-center">
              <Wallet className="w-12 h-12 mx-auto mb-3 opacity-80" />
              <p className="text-xs font-bold uppercase tracking-widest opacity-80 mb-1">Mon solde</p>
              <p className="text-5xl font-black" data-testid="credit-balance">{creditBalance.toLocaleString()} FCFA</p>
            </CardContent>
          </Card>

          {/* Tabs */}
          <div className="flex rounded-full bg-[#1E293B] border border-white/10 p-1 mb-6">
            <button
              type="button"
              className={`rounded-full flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-bold uppercase tracking-widest transition-all ${
                !showHistory
                  ? "bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0F172A] shadow-md"
                  : "text-gray-400 hover:text-white"
              }`}
              onClick={() => setShowHistory(false)}
              data-testid="tab-recharge"
            >
              <CreditCard className="w-4 h-4" />
              Recharger
            </button>
            <button
              type="button"
              className={`rounded-full flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-bold uppercase tracking-widest transition-all ${
                showHistory
                  ? "bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0F172A] shadow-md"
                  : "text-gray-400 hover:text-white"
              }`}
              onClick={() => setShowHistory(true)}
              data-testid="tab-history"
            >
              <History className="w-4 h-4" />
              Historique
            </button>
          </div>

          {!showHistory ? (
            <>
              {/* === 🌊 Wave Direct — Lien marchand ARTEKNOS (recharge rapide chauffeurs/livreurs) === */}
              {WAVE_MERCHANT_LINK && (user.role === 'courier' || user.role === 'taxi' || user.role === 'livreur' || user.role === 'partner') && (
                <Card className="mb-6 border-2 border-[#1EBED6] bg-gradient-to-br from-[#06283D] to-[#1EBED6]/20 text-white overflow-hidden relative rounded-[1.5rem]">
                  <div className="absolute -top-10 -right-10 w-32 h-32 bg-[#1EBED6] rounded-full blur-[70px] opacity-40"></div>
                  <CardContent className="relative z-10 pt-5 pb-5">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-11 h-11 rounded-xl bg-[#1EBED6] text-[#06283D] flex items-center justify-center font-black text-lg flex-shrink-0">
                          W
                        </div>
                        <div className="min-w-0">
                          <p className="font-black text-base">Payer la commission ARTEKNOS</p>
                          <p className="text-xs text-white/70 truncate">Lien marchand Wave officiel · paiement direct</p>
                        </div>
                      </div>
                      <a
                        href={WAVE_MERCHANT_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#1EBED6] text-[#06283D] text-xs font-black uppercase tracking-widest hover:bg-white transition-colors shadow-md"
                        data-testid="wave-direct-pay-btn"
                      >
                        <Zap className="w-4 h-4" />
                        Payer Wave
                      </a>
                    </div>
                    <p className="text-[10px] text-white/60 mt-3">
                      💡 Après paiement, envoyez la capture à l'admin pour créditer votre compte sous 2 min
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* === ⚡ PayTech Express : recharge instantanée Wave/Orange/Free/Carte === */}
              <Card className="mb-6 border-2 border-[#D4AF37] bg-gradient-to-br from-[#0F172A] to-[#003594] text-white overflow-hidden relative">
                <div className="absolute -top-12 -right-12 w-40 h-40 bg-[#D4AF37] rounded-full blur-[80px] opacity-30"></div>
                <CardContent className="relative z-10 pt-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-xl bg-[#D4AF37] text-[#0F172A] flex items-center justify-center">
                      <Zap className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-black text-lg">Recharge instantanée</p>
                      <p className="text-xs text-white/80">Wave · Orange Money · Free Money · Carte</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {[1000, 2000, 5000, 10000, 25000, 50000].map((amt) => (
                      <button
                        key={amt}
                        onClick={() => handleRechargePayTech(amt)}
                        disabled={isPayTechLoading}
                        className="p-3 rounded-xl bg-white/10 border border-white/20 text-white font-bold text-sm hover:bg-[#D4AF37] hover:text-[#0F172A] hover:border-[#D4AF37] transition-all disabled:opacity-50"
                        data-testid={`paytech-amount-${amt}`}
                      >
                        {amt.toLocaleString("fr-FR")}<span className="text-[10px] opacity-70 block">FCFA</span>
                      </button>
                    ))}
                  </div>
                  {isPayTechLoading && (
                    <div className="flex items-center gap-2 text-sm text-[#D4AF37]">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Préparation du paiement sécurisé…
                    </div>
                  )}
                  <p className="text-[10px] text-white/60 mt-2">
                    🔒 Paiement sécurisé via PayTech — crédit ajouté automatiquement dès confirmation
                  </p>
                </CardContent>
              </Card>

              {/* === Méthode manuelle (ancien flow conservé pour compatibilité) === */}
              <Card className="mb-6 bg-[#1E293B] border border-white/10 text-white rounded-[1.5rem]">
                <CardHeader>
                  <CardTitle className="text-sm font-black uppercase tracking-widest text-white">Autre méthode (manuelle)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-3">
                    {CREDIT_AMOUNTS.map((amount) => (
                      <button
                        key={amount}
                        onClick={() => setSelectedAmount(amount)}
                        className={`p-4 rounded-xl border-2 text-center font-bold transition-all ${
                          selectedAmount === amount
                            ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37] shadow-md scale-105'
                            : 'border-white/10 bg-[#0F172A] text-white hover:border-[#D4AF37]/50 hover:bg-[#D4AF37]/5'
                        }`}
                        data-testid={`amount-${amount}`}
                      >
                        {amount.toLocaleString()} FCFA
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Payment Method */}
              <Card className="mb-6 bg-[#1E293B] border border-white/10 text-white rounded-[1.5rem]">
                <CardHeader>
                  <CardTitle className="text-sm font-black uppercase tracking-widest text-white">Mode de paiement</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {PAYMENT_METHODS.map((method) => (
                      <button
                        key={method.id}
                        onClick={() => setSelectedMethod(method.id)}
                        className={`p-3 rounded-xl border-2 text-center font-medium transition-all ${
                          selectedMethod === method.id
                            ? 'border-[#D4AF37] bg-[#D4AF37]/10 shadow-md'
                            : 'border-white/10 bg-[#0F172A] hover:border-[#D4AF37]/50'
                        }`}
                        data-testid={`method-${method.id}`}
                      >
                        <div className={`w-8 h-8 ${method.color} rounded-full mx-auto mb-2`}></div>
                        <span className="text-white text-sm">{method.name}</span>
                      </button>
                    ))}
                  </div>

                  {selectedMethod && selectedMethod !== "cash" && (
                    <div className="mt-4">
                      <label className="text-xs font-bold uppercase tracking-widest text-gray-300 mb-1 block">Numéro de téléphone</label>
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-[#D4AF37]" />
                        <input
                          type="tel"
                          value={paymentPhone}
                          onChange={(e) => setPaymentPhone(e.target.value)}
                          placeholder="77 123 45 67"
                          className="flex-1 h-10 rounded-md border border-white/10 bg-[#0F172A] text-white placeholder:text-gray-500 px-3 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/40 focus:border-[#D4AF37]"
                          data-testid="payment-phone-input"
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Purchase Button */}
              <Button
                onClick={handlePurchase}
                disabled={!selectedAmount || !selectedMethod || isLoading}
                className="w-full bg-gradient-to-r from-[#D4AF37] to-[#B8962E] hover:opacity-90 text-[#0F172A] rounded-full py-6 text-sm font-black uppercase tracking-widest shadow-xl shadow-[#D4AF37]/20 disabled:opacity-40"
                data-testid="purchase-btn"
              >
                {isLoading ? "Traitement..." : selectedAmount
                  ? `Acheter ${selectedAmount.toLocaleString()} FCFA de crédit`
                  : "Sélectionnez un montant"}
              </Button>

              <p className="text-center text-xs text-gray-500 mt-3 uppercase tracking-widest font-bold">
                Montant minimum : 500 FCFA
              </p>

              {/* Wave Confirmation Dialog */}
              {showWaveConfirm && (
                <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="wave-confirm-dialog">
                  <div className="bg-[#1E293B] border border-white/10 rounded-[2rem] p-6 max-w-md w-full shadow-2xl text-white">
                    <div className="text-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-br from-[#D4AF37] to-[#B8962E] rounded-full mx-auto mb-4 flex items-center justify-center shadow-lg shadow-[#D4AF37]/30">
                        <CreditCard className="w-8 h-8 text-[#0F172A]" />
                      </div>
                      <h3 className="text-lg font-black uppercase tracking-tight mb-2 text-white">Confirmation de paiement</h3>
                      <p className="text-sm text-gray-300">
                        Avez-vous effectué le paiement de <strong className="text-[#D4AF37]">{selectedAmount?.toLocaleString()} FCFA</strong> via {selectedMethod === "wave" ? "Wave" : selectedMethod === "orange_money" ? "Orange Money" : "Free Money"} ?
                      </p>
                    </div>
                    <div className="flex gap-3">
                      <Button
                        onClick={confirmWavePayment}
                        disabled={isLoading}
                        className="flex-1 bg-gradient-to-r from-[#D4AF37] to-[#B8962E] hover:opacity-90 text-[#0F172A] rounded-full font-black uppercase tracking-widest text-xs"
                        data-testid="confirm-payment-btn"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        {isLoading ? "Confirmation..." : "Oui, j'ai payé"}
                      </Button>
                      <Button
                        onClick={() => { setShowWaveConfirm(false); setPendingTransactionId(null); }}
                        variant="outline"
                        className="flex-1 rounded-full bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-[#D4AF37] hover:border-[#D4AF37] font-bold uppercase tracking-widest text-xs"
                        data-testid="cancel-payment-btn"
                      >
                        Annuler
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* History */
            <Card className="bg-[#1E293B] border border-white/10 text-white rounded-[1.5rem]">
              <CardHeader>
                <CardTitle className="text-sm font-black uppercase tracking-widest text-white">Historique des transactions</CardTitle>
              </CardHeader>
              <CardContent>
                {history.length === 0 ? (
                  <p className="text-center py-8 text-gray-500 uppercase tracking-widest text-xs font-bold">Aucune transaction</p>
                ) : (
                  <div className="space-y-3">
                    {history.map((tx) => {
                      const debit = (tx.amount || 0) < 0;
                      const label = tx.type === "commission_debit" ? "Commission ARTEKNOS 10 %"
                        : tx.type === "artisan_search_fee" ? "Pass artisans"
                        : debit ? "Débit" : "Recharge crédit";
                      return (
                      <div key={tx.id} className="flex items-center justify-between gap-3 p-3 border border-white/10 bg-[#0F172A] rounded-xl" data-testid={`tx-${tx.id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          {debit ? <MinusCircle className="w-5 h-5 text-rose-400 shrink-0" /> : <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0" />}
                          <div className="min-w-0">
                            <p className="font-semibold text-sm text-white">{label}</p>
                            {debit && tx.description && <p className="text-xs text-slate-300 break-words" data-testid={`tx-desc-${tx.id}`}>{tx.description}</p>}
                            <p className="text-xs text-gray-400">
                              {debit ? "" : `${PAYMENT_METHODS.find(m => m.id === tx.payment_method)?.name || tx.payment_method || ""} - `}{new Date(tx.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                        <span className={`font-black shrink-0 ${debit ? "text-rose-400" : "text-emerald-400"}`}>{debit ? "" : "+"}{tx.amount?.toLocaleString()} FCFA</span>
                      </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
